import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import {
  getScreenerTickers,
  addScreenerTicker,
  removeScreenerTicker,
  getScreenerData,
  getScreenerOIData,
  updateScreenerSettings,
} from '@/lib/api'
import type {
  ScreenerTicker,
  ScreenerSettings,
  ScreenerTickerData,
  ScreenerCandle,
  ScreenerExchangeData,
  ScreenerOIDataPoint,
  ScreenerVolumeDataPoint,
} from '@/types'
import { FALLBACK_EXCHANGES } from '@/types'
import { bufferedHistoryRange, initialHistoryRange } from '@/lib/screenerHistory'

interface UseScreenerDataResult {
  // State
  tickers: ScreenerTicker[]
  hasLoadedTickers: boolean
  activeTicker: string | null
  activeTickerData: ScreenerTickerData | null
  settings: ScreenerSettings
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null

  // Actions
  addTicker: (symbol: string) => Promise<boolean>
  removeTicker: (symbol: string) => Promise<boolean>
  setActiveTicker: (symbol: string | null) => void
  updateSettings: (settings: Partial<ScreenerSettings>) => Promise<boolean>
  refresh: () => Promise<void>
}

const DEFAULT_SETTINGS: ScreenerSettings = {
  period: '1m',
  exchanges: FALLBACK_EXCHANGES,
  markets: ['spot', 'futures'],
  chart_filters: {
    exchanges: FALLBACK_EXCHANGES,
    markets: ['spot', 'futures'],
  },
  orderbook_filters: {
    exchanges: FALLBACK_EXCHANGES,
    markets: ['spot', 'futures'],
  },
  candle_limit: 200,
}

const POLL_INTERVAL = 5000 // 5 seconds
const OI_DELTA_POLL_INTERVAL = 1000 // observe each committed 5s bucket promptly

type OIRevision = {
  timestamp?: number | null
  sampleKind?: string | null
  quality?: string | null
  observedAt?: number | null
}

function finiteRevisionValue(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function monotonicOISourceAge(
  current: number | null | undefined,
  incoming: number | null | undefined,
): number | null | undefined {
  const currentIsFinite = typeof current === 'number' && Number.isFinite(current)
  const incomingIsFinite = typeof incoming === 'number' && Number.isFinite(incoming)
  if (currentIsFinite && incomingIsFinite) return Math.max(current, incoming)
  if (incomingIsFinite) return incoming
  return current
}

function oiQualityRank(quality: string | null | undefined): number {
  switch (String(quality || '').trim().toLowerCase()) {
    case 'fresh':
      return 3
    case 'observed':
    case 'observed_snapshot':
      return 2
    case 'carried':
    case 'carried_forward':
      return 1
    default:
      return 0
  }
}

function oiSampleKindRank(sampleKind: string | null | undefined): number {
  switch (String(sampleKind || '').trim().toLowerCase()) {
    case 'continuous':
      return 3
    case 'live':
      return 2
    case 'background':
      return 1
    default:
      return 0
  }
}

function compareOIRevision(left: OIRevision, right: OIRevision): number {
  const timestampDelta = finiteRevisionValue(left.timestamp) - finiteRevisionValue(right.timestamp)
  if (timestampDelta !== 0) return timestampDelta
  const sampleKindDelta = oiSampleKindRank(left.sampleKind) - oiSampleKindRank(right.sampleKind)
  if (sampleKindDelta !== 0) return sampleKindDelta
  // Continuous samples describe actual observations. A later receipt may
  // reveal a stale source, so its lower quality must not preserve an old value.
  const observedDelta = finiteRevisionValue(left.observedAt) - finiteRevisionValue(right.observedAt)
  if (left.sampleKind === 'continuous' && observedDelta !== 0) return observedDelta
  const qualityDelta = oiQualityRank(left.quality) - oiQualityRank(right.quality)
  if (qualityDelta !== 0) return qualityDelta
  return observedDelta
}

function exchangeOIRevision(data: Partial<ScreenerExchangeData>): OIRevision {
  const history = data.oi_history || []
  const latest = history.length > 0 ? history[history.length - 1] : undefined
  return {
    timestamp: data.oi_updated_at ?? latest?.timestamp,
    sampleKind: data.oi_sample_kind ?? latest?.sampleKind,
    quality: data.oi_quality ?? latest?.quality,
    observedAt: data.oi_observed_at ?? latest?.observedAt,
  }
}

function hasOIRevision(data: Partial<ScreenerExchangeData>): boolean {
  return data.oi_updated_at != null
    || data.oi_observed_at != null
    || (data.oi_history?.length || 0) > 0
}

/** Funding can change without a new OI sample; compare the ticker snapshots independently. */
function mergeFundingSnapshot(
  existing: Partial<ScreenerExchangeData>,
  incoming: Partial<ScreenerExchangeData>,
): Pick<ScreenerExchangeData, 'funding_rate' | 'funding_time' | 'funding_updated_at'> {
  const source = incoming.funding_rate === undefined
    || (existing.funding_rate !== undefined
      && finiteRevisionValue(existing.funding_updated_at) > finiteRevisionValue(incoming.funding_updated_at))
    ? existing : incoming
  return {
    funding_rate: source.funding_rate,
    funding_time: source.funding_time,
    funding_updated_at: source.funding_updated_at,
  }
}

/** Trade segments can arrive after OI; merge their bucket revisions independently. */
function mergeVolumeHistory(
  existing: Partial<ScreenerExchangeData>,
  incoming: Partial<ScreenerExchangeData>,
  oiHistory: ScreenerOIDataPoint[],
): Pick<ScreenerExchangeData, 'volume_history' | 'volume_interval' | 'volume_updated_at'> {
  if (incoming.volume_history === undefined) return {
    volume_history: existing.volume_history,
    volume_interval: existing.volume_interval,
    volume_updated_at: existing.volume_updated_at,
  }
  const histories = existing.volume_interval === incoming.volume_interval ? [existing, incoming] : [incoming]
  const byTimestamp = new Map<number, ScreenerVolumeDataPoint>()
  for (const snapshot of histories) {
    for (const point of snapshot.volume_history || []) {
      if (!Number.isFinite(point.timestamp)) continue
      const previous = byTimestamp.get(point.timestamp)
      const updatedAt = point.updatedAt ?? snapshot.volume_updated_at ?? 0
      if (!previous || finiteRevisionValue(updatedAt) >= finiteRevisionValue(previous.updatedAt)) {
        // Explicit unavailable buckets also replace earlier values; never forward-fill traded volume.
        byTimestamp.set(point.timestamp, { ...point, updatedAt })
      }
    }
  }
  const sorted = [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp)
  const visibleOI = new Set(oiHistory.map((point) => point.timestamp))
  return {
    // A sparse OI source can retain older visible points while volume keeps
    // updating. Keep their exact buckets as well as the latest 500 (at most 1,000).
    volume_history: sorted.filter((point, index) => index >= sorted.length - 500 || visibleOI.has(point.timestamp)),
    volume_interval: incoming.volume_interval,
    volume_updated_at: Math.max(...histories.map((data) => finiteRevisionValue(data.volume_updated_at))) || null,
  }
}

function mergeOIHistory(
  existingHistory: ScreenerOIDataPoint[] = [],
  incomingHistory: ScreenerOIDataPoint[] = [],
): ScreenerOIDataPoint[] {
  const historyByTimestamp = new Map<number, ScreenerOIDataPoint>()
  for (const point of [...existingHistory, ...incomingHistory]) {
    const existing = historyByTimestamp.get(point.timestamp)
    if (
      !existing
      || compareOIRevision(
        {
          timestamp: point.timestamp,
          sampleKind: point.sampleKind,
          quality: point.quality,
          observedAt: point.observedAt,
        },
        {
          timestamp: existing.timestamp,
          sampleKind: existing.sampleKind,
          quality: existing.quality,
          observedAt: existing.observedAt,
        },
      ) > 0
    ) {
      historyByTimestamp.set(point.timestamp, point)
    }
  }
  return Array.from(historyByTimestamp.values())
    .sort((a, b) => a.timestamp - b.timestamp)
    .slice(-500)
}

function normalizedContextValues(values: string[] | undefined): string[] {
  return (values || [])
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean)
    .sort()
}

function screenerChartContextKey(
  activeTicker: string | null,
  settings: ScreenerSettings,
  includeOIOhlc: boolean,
): string {
  const timeframe = settings.timeframe
  return JSON.stringify({
    ticker: activeTicker?.trim().toUpperCase() || null,
    period: settings.period,
    includeOIOhlc,
    timeframe: {
      mode: timeframe?.mode ?? 'live',
      relativeSeconds: timeframe?.relative_seconds ?? null,
      from: timeframe?.from_ts ?? null,
      to: timeframe?.to_ts ?? null,
    },
    candleLimit: settings.candle_limit,
    chartExchanges: normalizedContextValues(
      settings.chart_filters?.exchanges ?? settings.exchanges,
    ),
    chartMarkets: normalizedContextValues(
      settings.chart_filters?.markets ?? settings.markets,
    ),
  })
}

export function useScreenerData(includeOrderbook = false, includeOIOhlc = false): UseScreenerDataResult {
  const [tickers, setTickers] = useState<ScreenerTicker[]>([])
  const [hasLoadedTickers, setHasLoadedTickers] = useState(false)
  const [activeTicker, setActiveTicker] = useState<string | null>(null)
  const [activeTickerData, setActiveTickerData] = useState<ScreenerTickerData | null>(null)
  const [settings, setSettings] = useState<ScreenerSettings>(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const chartDataContextKey = screenerChartContextKey(activeTicker, settings, includeOIOhlc)
  const dataQueryContextKey = JSON.stringify({
    chart: chartDataContextKey,
    orderbookExchanges: normalizedContextValues(settings.orderbook_filters?.exchanges),
    orderbookMarkets: normalizedContextValues(settings.orderbook_filters?.markets),
    includeOrderbook,
  })

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const oiPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fullAbortRef = useRef<AbortController | null>(null)
  const fullRequestRef = useRef<{ context: string; promise: Promise<void> } | null>(null)
  const oiAbortRef = useRef<AbortController | null>(null)
  const fullSequenceRef = useRef(0)
  const oiSequenceRef = useRef(0)
  const activeQueryContextRef = useRef<string | null>(null)
  const activeDataContextRef = useRef<string | null>(null)
  // Fetch tickers list and settings
  const fetchTickers = useCallback(async (includeSettings = true, signal?: AbortSignal) => {
    try {
      const result = await getScreenerTickers(signal)
      if (signal?.aborted) return 'stop'
      setTickers(result.tickers || [])
      if (includeSettings && result.settings) {
        const incomingSettings = result.settings
        setSettings((current) => JSON.stringify(current) === JSON.stringify(incomingSettings)
          ? current : incomingSettings)
      }
      setHasLoadedTickers(true)
      setError(null)
      return 'loaded'
    } catch (err) {
      if (signal?.aborted) return 'stop'
      console.error('Failed to fetch screener tickers:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch tickers')
      const status = err && typeof err === 'object' && 'status' in err ? err.status : undefined
      if ((err instanceof Error && (err.message === 'Unauthorized' || err.name === 'AbortError'))
        || (typeof status === 'number' && status < 500 && status !== 408 && status !== 429)) return 'stop'
      return 'retry'
    }
  }, [])

  // Fetch data for active ticker
  const loadActiveTickerData = useCallback(async (signal: AbortSignal, sequence: number) => {
    if (!activeTicker) return
    const isCurrent = () => !signal.aborted && sequence === fullSequenceRef.current
      && activeQueryContextRef.current === dataQueryContextKey
    try {
      const historicalWindow = settings.timeframe && settings.timeframe.mode !== 'live'
        ? bufferedHistoryRange(initialHistoryRange(null, settings.period, settings.timeframe))
        : undefined
      const result = await getScreenerData(
        activeTicker,
        settings.timeframe,
        settings.period,
        includeOrderbook,
        signal,
        (settings.chart_filters?.markets ?? settings.markets)?.length === 0
          ? [] : settings.chart_filters?.exchanges ?? settings.exchanges ?? [],
        historicalWindow,
        includeOIOhlc,
      )
      if (!isCurrent()) return
      if (result.ok && result.data) {
        const incoming = result.data
        const canMergeCurrent = activeDataContextRef.current === chartDataContextKey
          && (settings.timeframe?.mode ?? 'live') === 'live'
        setActiveTickerData((current) => {
          if (
            !canMergeCurrent
            || !current
            || current.symbol.toUpperCase() !== incoming.symbol.toUpperCase()
          ) {
            return incoming
          }
          const exchanges = { ...incoming.exchanges }
          for (const [exchange, currentData] of Object.entries(current.exchanges)) {
            const incomingData = exchanges[exchange]
            if (!incomingData) {
              exchanges[exchange] = currentData
              continue
            }
            const mergedHistory = mergeOIHistory(
              currentData.oi_history,
              incomingData.oi_history,
            )
            const revisionComparison = compareOIRevision(
              exchangeOIRevision(currentData),
              exchangeOIRevision(incomingData),
            )
            if (
              hasOIRevision(currentData)
              && revisionComparison >= 0
            ) {
              exchanges[exchange] = {
                ...incomingData,
                ...mergeFundingSnapshot(currentData, incomingData),
                ...mergeVolumeHistory(currentData, incomingData, mergedHistory),
                oi_current: currentData.oi_current,
                oi_current_usdt: currentData.oi_current_usdt,
                oi_history: mergedHistory,
                oi_updated_at: currentData.oi_updated_at,
                oi_observed_at: currentData.oi_observed_at,
                oi_quality: currentData.oi_quality,
                oi_sample_kind: currentData.oi_sample_kind,
                // A response with the same OI revision can still carry a
                // newer source-age calculation.  Keeping the older age made
                // a stopped collector look permanently fresh in the UI.
                oi_source_age_seconds: revisionComparison === 0
                  ? monotonicOISourceAge(
                      currentData.oi_source_age_seconds,
                      incomingData.oi_source_age_seconds,
                    )
                  : currentData.oi_source_age_seconds,
              }
            } else {
              exchanges[exchange] = {
                ...incomingData,
                ...mergeFundingSnapshot(currentData, incomingData),
                ...mergeVolumeHistory(currentData, incomingData, mergedHistory),
                oi_history: mergedHistory,
              }
            }
          }
          return {
            ...incoming,
            updated_at: Math.max(incoming.updated_at || 0, current.updated_at || 0) || null,
            oi_history_until: Math.max(
              finiteRevisionValue(incoming.oi_history_until),
              finiteRevisionValue(current.oi_history_until),
            ) || null,
            exchanges,
          }
        })
        activeDataContextRef.current = chartDataContextKey
        const incomingLastUpdated = result.data.updated_at
          ? new Date(result.data.updated_at * 1000)
          : new Date()
        setLastUpdated((current) => (
          current && current.getTime() >= incomingLastUpdated.getTime()
            ? current
            : incomingLastUpdated
        ))
        setError(null)
      } else {
        setError(result.error || 'Failed to fetch ticker data')
      }
    } catch (err) {
      if (!isCurrent()) return
      console.error('Failed to fetch ticker data:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data')
    }
  }, [
    activeTicker,
    settings.timeframe,
    settings.period,
    includeOrderbook,
    includeOIOhlc,
    settings.chart_filters?.exchanges,
    settings.chart_filters?.markets,
    settings.exchanges,
    settings.markets,
    dataQueryContextKey,
    chartDataContextKey,
  ])

  const fetchActiveTickerData = useCallback((): Promise<void> => {
    if (!activeTicker) {
      setActiveTickerData(null)
      return Promise.resolve()
    }
    // The refresh button and scheduled poll share one request. Every full read
    // belongs to the view, including reads started manually before switching coins.
    if (fullRequestRef.current?.context === dataQueryContextKey && !fullAbortRef.current?.signal.aborted) {
      return fullRequestRef.current.promise
    }
    fullAbortRef.current?.abort()
    const controller = new AbortController()
    fullAbortRef.current = controller
    const sequence = ++fullSequenceRef.current
    const promise = loadActiveTickerData(controller.signal, sequence).finally(() => {
      if (fullRequestRef.current?.promise === promise) fullRequestRef.current = null
    })
    fullRequestRef.current = { context: dataQueryContextKey, promise }
    return promise
  }, [activeTicker, dataQueryContextKey, loadActiveTickerData])

  const fetchActiveOIDelta = useCallback(async (signal?: AbortSignal) => {
    if (
      !activeTicker
      || activeDataContextRef.current !== chartDataContextKey
      || settings.period !== '5s'
      || (settings.timeframe?.mode ?? 'live') !== 'live'
      || (settings.chart_filters?.markets ?? settings.markets)?.length === 0
      || (settings.chart_filters?.exchanges ?? settings.exchanges)?.length === 0
    ) return
    const sequence = ++oiSequenceRef.current
    try {
      const result = await getScreenerOIData(
        activeTicker,
        settings.period,
        signal,
        settings.chart_filters?.exchanges ?? settings.exchanges,
        includeOIOhlc,
      )
      if (
        signal?.aborted
        || sequence !== oiSequenceRef.current
        || activeQueryContextRef.current !== dataQueryContextKey
        || activeDataContextRef.current !== chartDataContextKey
      ) return
      if (!result.ok || !result.data) {
        if (result.error) setError(result.error)
        return
      }

      const delta = result.data
      setActiveTickerData((current) => {
        if (!current || current.symbol.toUpperCase() !== delta.symbol.toUpperCase()) {
          return current
        }
        const exchanges = { ...current.exchanges }
        for (const [exchange, oiData] of Object.entries(delta.exchanges)) {
          const existing = exchanges[exchange] || {
              candles: [],
              oi_current: null,
              oi_current_usdt: null,
              last_price: null,
              volume_24h: null,
              updated_at: null,
            }
          const mergedHistory = mergeOIHistory(existing.oi_history, oiData.oi_history)
          const revisionComparison = compareOIRevision(
            exchangeOIRevision(oiData),
            exchangeOIRevision(existing),
          )
          const deltaWins = !hasOIRevision(existing) || revisionComparison > 0
          exchanges[exchange] = {
            ...existing,
            ...(deltaWins ? oiData : {}),
            ...(!deltaWins && revisionComparison === 0
              ? {
                  oi_source_age_seconds:
                    monotonicOISourceAge(
                      existing.oi_source_age_seconds,
                      oiData.oi_source_age_seconds,
                    ),
                }
              : {}),
            oi_history: mergedHistory,
            ...mergeFundingSnapshot(existing, oiData),
            ...mergeVolumeHistory(existing, oiData, mergedHistory),
          }
        }
        return {
          ...current,
          updated_at: Math.max(current.updated_at || 0, delta.updated_at || 0) || null,
          oi_history_until: Math.max(
            finiteRevisionValue(current.oi_history_until),
            finiteRevisionValue(delta.oi_history_until),
          ) || null,
          live_oi: delta.live_oi,
          exchanges,
        }
      })
      if (delta.updated_at) {
        const deltaLastUpdated = new Date(delta.updated_at * 1000)
        setLastUpdated((current) => (
          current && current.getTime() >= deltaLastUpdated.getTime()
            ? current
            : deltaLastUpdated
        ))
      }
      setError(null)
    } catch (err) {
      if (signal?.aborted || sequence !== oiSequenceRef.current
        || activeQueryContextRef.current !== dataQueryContextKey) return
      console.error('Failed to fetch screener OI delta:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch OI data')
    }
  }, [
    activeTicker,
    settings.period,
    includeOIOhlc,
    settings.timeframe?.mode,
    settings.chart_filters?.exchanges,
    settings.chart_filters?.markets,
    settings.exchanges,
    settings.markets,
    dataQueryContextKey,
    chartDataContextKey,
  ])

  // Full refresh
  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      // The view already owns its settings. Rehydrating them here can restart
      // polling midway through refresh and restore another tab's preferences.
      await Promise.all([fetchTickers(false), fetchActiveTickerData()])
    } finally {
      setIsLoading(false)
    }
  }, [fetchTickers, fetchActiveTickerData, activeTicker])

  // Add ticker
  const addTicker = useCallback(async (symbol: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await addScreenerTicker(
        symbol.toUpperCase(),
        settings.chart_filters?.exchanges || settings.exchanges
      )
      if (result.ok) {
        await fetchTickers()
        // Auto-select backend-normalized symbol if available.
        const normalizedSymbol = (result.symbol || symbol).toUpperCase()
        setActiveTicker(normalizedSymbol)
        return true
      } else {
        setError(result.error || 'Failed to add ticker')
        return false
      }
    } catch (err) {
      console.error('Failed to add ticker:', err)
      setError(err instanceof Error ? err.message : 'Failed to add ticker')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [settings.chart_filters?.exchanges, settings.exchanges, fetchTickers])

  // Remove ticker
  const removeTicker = useCallback(async (symbol: string): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await removeScreenerTicker(symbol)
      if (result.ok) {
        // If removing the active ticker, switch to another one
        if (activeTicker === symbol) {
          const remaining = tickers.filter(t => t.symbol !== symbol)
          setActiveTicker(remaining.length > 0 ? remaining[0].symbol : null)
        }
        await fetchTickers()
        return true
      } else {
        setError(result.error || 'Failed to remove ticker')
        return false
      }
    } catch (err) {
      console.error('Failed to remove ticker:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove ticker')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [activeTicker, tickers, fetchTickers])

  // Update settings
  const updateSettingsAction = useCallback(async (newSettings: Partial<ScreenerSettings>): Promise<boolean> => {
    try {
      setIsLoading(true)
      const result = await updateScreenerSettings(newSettings)
      if (result.ok && result.settings) {
        setSettings(result.settings)
        return true
      } else {
        setError(result.error || 'Failed to update settings')
        return false
      }
    } catch (err) {
      console.error('Failed to update settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to update settings')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Recover a transient bootstrap failure without retrying mutations or
  // hiding the current error. Each read settles before its backoff begins.
  useEffect(() => {
    if (hasLoadedTickers) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | null = null
    let delay = 1000
    const bootstrap = async () => {
      const outcome = await fetchTickers(true, controller.signal)
      if (controller.signal.aborted || outcome !== 'retry') return
      timer = setTimeout(bootstrap, delay)
      delay = Math.min(delay * 2, 30_000)
    }
    void bootstrap()
    return () => {
      controller.abort()
      if (timer !== null) clearTimeout(timer)
    }
  }, [fetchTickers, hasLoadedTickers])

  // Reject outdated requests when optional orderbook data changes, but keep the
  // current charts mounted while the replacement request is in flight.
  useLayoutEffect(() => {
    activeQueryContextRef.current = dataQueryContextKey
  }, [dataQueryContextKey])

  // Never merge chart history across ticker, interval, timeframe, or chart filters.
  useLayoutEffect(() => {
    activeDataContextRef.current = null
    setActiveTickerData(null)
    setLastUpdated(null)
  }, [chartDataContextKey])

  // Poll for data when there's an active ticker
  useEffect(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
    fullAbortRef.current?.abort()
    oiAbortRef.current?.abort()
    fullSequenceRef.current += 1
    oiSequenceRef.current += 1

    let cancelled = false
    let fullInFlight = false
    let oiInFlight = false
    const visibilityDocument = typeof document === 'undefined' ? null : document
    let visible = visibilityDocument?.visibilityState !== 'hidden'
    const pollLiveOI = settings.period === '5s'
      && (settings.timeframe?.mode ?? 'live') === 'live'
    const pollFullContinuously = settings.timeframe?.mode !== 'absolute'
      || (settings.timeframe.to_ts ?? 0) >= Date.now() / 1000 || includeOrderbook
    const pollFull = async () => {
      if (cancelled || !visible || fullInFlight) return
      fullInFlight = true
      pollTimeoutRef.current = null
      try {
        await fetchActiveTickerData()
      } finally {
        fullInFlight = false
        if (!cancelled && visible && pollFullContinuously) {
          pollTimeoutRef.current = setTimeout(pollFull, POLL_INTERVAL)
        }
        if (!cancelled && visible && pollLiveOI && !oiInFlight && !oiPollTimeoutRef.current
          && activeDataContextRef.current === chartDataContextKey) {
          void pollOI()
        }
      }
    }
    const pollOI = async () => {
      if (cancelled || !visible || oiInFlight) return
      oiInFlight = true
      oiPollTimeoutRef.current = null
      const controller = new AbortController()
      oiAbortRef.current = controller
      try {
        await fetchActiveOIDelta(controller.signal)
      } finally {
        oiInFlight = false
        if (!cancelled && visible) {
          oiPollTimeoutRef.current = setTimeout(pollOI, OI_DELTA_POLL_INTERVAL)
        }
      }
    }
    const startPolling = () => {
      if (!activeTicker) return
      void pollFull()
      // A delta cannot be merged until the matching full chart exists. Starting
      // it earlier competes with initial load and can show a discarded read's timeout.
      if (pollLiveOI && activeDataContextRef.current === chartDataContextKey) void pollOI()
    }
    const onVisibilityChange = () => {
      const nextVisible = visibilityDocument?.visibilityState !== 'hidden'
      if (visible === nextVisible) return
      visible = nextVisible
      if (visible) {
        startPolling()
      } else {
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
        if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
        pollTimeoutRef.current = null
        oiPollTimeoutRef.current = null
        // Let in-flight reads finish: aborting fetch does not cancel server SQL,
        // and returning to the tab could otherwise repeat that expensive work.
      }
    }
    visibilityDocument?.addEventListener('visibilitychange', onVisibilityChange)
    startPolling()

    return () => {
      cancelled = true
      visibilityDocument?.removeEventListener('visibilitychange', onVisibilityChange)
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
      if (oiPollTimeoutRef.current) clearTimeout(oiPollTimeoutRef.current)
      pollTimeoutRef.current = null
      oiPollTimeoutRef.current = null
      fullAbortRef.current?.abort()
      oiAbortRef.current?.abort()
    }
  }, [
    activeTicker,
    fetchActiveTickerData,
    fetchActiveOIDelta,
    dataQueryContextKey,
    settings.period,
    settings.timeframe?.mode,
    includeOrderbook,
  ])

  return {
    tickers,
    hasLoadedTickers,
    activeTicker,
    activeTickerData,
    settings,
    isLoading,
    error,
    lastUpdated,
    addTicker,
    removeTicker,
    setActiveTicker,
    updateSettings: updateSettingsAction,
    refresh,
  }
}

// Helper to aggregate candles from multiple exchanges
export function aggregateCandles(
  exchangesData: Record<string, { candles: ScreenerCandle[] }>
): ScreenerCandle[] {
  // Get all unique timestamps
  const timestampMap = new Map<number, {
    open: number[]
    high: number[]
    low: number[]
    close: number[]
    volume: number[]
  }>()

  for (const exchangeData of Object.values(exchangesData)) {
    for (const candle of exchangeData.candles || []) {
      if (!timestampMap.has(candle.timestamp)) {
        timestampMap.set(candle.timestamp, {
          open: [],
          high: [],
          low: [],
          close: [],
          volume: [],
        })
      }
      const entry = timestampMap.get(candle.timestamp)!
      entry.open.push(candle.open)
      entry.high.push(candle.high)
      entry.low.push(candle.low)
      entry.close.push(candle.close)
      entry.volume.push(candle.volume)
    }
  }

  // Aggregate into single candles (average prices, sum volumes)
  const aggregated: ScreenerCandle[] = []
  for (const [timestamp, data] of timestampMap) {
    const avgOpen = data.open.reduce((a, b) => a + b, 0) / data.open.length
    const avgHigh = Math.max(...data.high)
    const avgLow = Math.min(...data.low)
    const avgClose = data.close.reduce((a, b) => a + b, 0) / data.close.length
    const totalVolume = data.volume.reduce((a, b) => a + b, 0)

    aggregated.push({
      timestamp,
      open: avgOpen,
      high: avgHigh,
      low: avgLow,
      close: avgClose,
      volume: totalVolume,
    })
  }

  // Sort by timestamp
  aggregated.sort((a, b) => a.timestamp - b.timestamp)

  return aggregated
}

// Helper to build OI history from current values
export function buildOIHistory(
  exchangesData: Record<string, {
    oi_current: number | null
    oi_current_usdt?: number | null
    oi_history?: Array<{ timestamp: number; value: number; valueUsdt?: number | null }>
    candles: ScreenerCandle[]
  }>
): Array<{ timestamp: number; value: number; valueUsdt?: number }> {
  const oiHistoryMap = new Map<number, { value: number; valueUsdt: number }>()

  // Prefer backend-provided OI history if available.
  for (const data of Object.values(exchangesData)) {
    const history = data.oi_history || []
    if (history.length === 0) continue
    for (const point of history) {
      const timestamp = Number(point.timestamp || 0)
      if (!timestamp) continue
      const existing = oiHistoryMap.get(timestamp) || { value: 0, valueUsdt: 0 }
      existing.value += Number(point.value || 0)
      existing.valueUsdt += Number(point.valueUsdt || 0)
      oiHistoryMap.set(timestamp, existing)
    }
  }

  if (oiHistoryMap.size > 0) {
    return Array.from(oiHistoryMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([timestamp, values]) => ({
        timestamp,
        value: values.value,
        valueUsdt: values.valueUsdt > 0 ? values.valueUsdt : undefined,
      }))
  }

  // Fallback: synthesize flat series from current OI and candle timestamps.
  let totalOI = 0
  let totalOIUsdt = 0
  for (const data of Object.values(exchangesData)) {
    if (data.oi_current) totalOI += data.oi_current
    if (data.oi_current_usdt) totalOIUsdt += data.oi_current_usdt
  }
  const allCandles = Object.values(exchangesData).flatMap(d => d.candles || [])
  if (allCandles.length === 0 || (totalOI <= 0 && totalOIUsdt <= 0)) {
    return []
  }
  return allCandles.slice(-40).map(c => ({
    timestamp: c.timestamp,
    value: totalOI,
    valueUsdt: totalOIUsdt > 0 ? totalOIUsdt : undefined,
  }))
}
